import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Padrão único de página:
 *  • Desktop: container centralizado, padding consistente (px-8 py-8), largura máx.
 *  • Mobile : tela de app — largura total, padding compacto (px-4 py-4), scroll com
 *             momentum; o clearance do bottom-nav já vem do <main> do AppShell.
 *
 * Use sempre como wrapper externo das páginas de conteúdo (dashboard, projects, etc.).
 */
const MAX_WIDTHS = {
  sm: 'max-w-2xl',
  md: 'max-w-3xl',
  lg: 'max-w-5xl',
  xl: 'max-w-6xl',
  '2xl': 'max-w-7xl',
  full: 'max-w-none',
} as const;

interface PageShellProps {
  children: React.ReactNode;
  className?: string;
  /** largura máxima do conteúdo no desktop (default: lg) */
  width?: keyof typeof MAX_WIDTHS;
}

export function PageShell({ children, className, width = 'lg' }: PageShellProps) {
  return (
    <div className="flex-1 overflow-y-auto [-webkit-overflow-scrolling:touch]">
      <div className={cn('mx-auto w-full px-4 py-4 md:px-8 md:py-8', MAX_WIDTHS[width], className)}>
        {children}
      </div>
    </div>
  );
}

interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** ações à direita (botões). No mobile descem para baixo do título. */
  actions?: React.ReactNode;
  className?: string;
}

export function PageHeader({ title, description, actions, className }: PageHeaderProps) {
  return (
    <div
      className={cn(
        'mb-6 flex flex-col gap-3 md:mb-8 md:flex-row md:items-start md:justify-between md:gap-4',
        className
      )}
    >
      <div className="min-w-0">
        <h1 className="font-serif text-2xl font-bold tracking-tight text-foreground md:text-3xl">
          {title}
        </h1>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground md:text-base">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
